import { createElement } from 'react';
import {
	ETableStructureAlign,
	ETableStructureType,
	TTableStructure,
} from 'services/constants/tableStructureTypes';
import { DATES, DATES_ID } from 'services/constants/dates';
import { representationSchemeType } from 'services/constants/representationSchemeType';
import { AmountSpeedDialMemo } from 'components/Shipments/ShipmentsRow/AmountSpeedDial';
import { TableCellSpeedDial } from 'components/Shipments/ShipmentsRow/TableCellSpeedDial';
import { TEuDdpOrderResponseData } from 'redux/reducers/euDdp/types';
import { EIconButtons } from 'services/constants/iconsTypes';
import {
	EEuDdpOrderShippingStatusCode,
	EEuDdpOrderShippingStatusName,
} from 'redux/reducers/euDdp/constants';

export const SHIPMENTS_ACTIONS = {
	CONFIRM_PAYMENT: 'Confirm Payment',
	OPEN: 'Open Shipment',
	REQUEST_DELIVERY: 'Request Swiss Post Delivery',
	CHECK_ORDER: 'Check order information',
	VALIDATE_HS: 'Validate Harmonized System (HS) codes',
	SIGN_PROFORMA: 'Sign Pro-Forma Invoice',
	UPLOAD_IOSS_INVOICE: 'Upload Invoice for IOSS order',
	PROVIDE_S10_NUMBER: 'Provide S10 Number',
	TRACKING: 'Tracking',
	GET_LAST_MILE_TRACKING: 'Get last mile tracking',
} as const;

const DATE_FILTER: [string, string][] = [
	[DATES_ID.TODAY, DATES.TODAY],
	[DATES_ID.YESTERDAY, DATES.YESTERDAY],
	[DATES_ID.LAST_7_DAYS, DATES.LAST_7_DAYS],
	[DATES_ID.LAST_30_DAYS, DATES.LAST_30_DAYS],
	[DATES_ID.THIS_MONTH, DATES.THIS_MONTH],
	[DATES_ID.LAST_MONTH, DATES.LAST_MONTH],
	[DATES_ID.THIS_QUARTER, DATES.THIS_QUARTER],
	[DATES_ID.LAST_QUARTER, DATES.LAST_QUARTER],
	[DATES_ID.THIS_YEAR, DATES.THIS_YEAR],
	[DATES_ID.LAST_YEAR, DATES.LAST_YEAR],
	[DATES_ID.CUSTOM_RANGE_PAST, DATES.CUSTOM_RANGE],
];

export const SCHEMES_FILTER: [string, string][] = Object.entries(representationSchemeType).map(
	([key, value]) => [key, value],
);

export const SCHEMES_COLORS: [string, string][] = Object.keys(representationSchemeType).map(
	(key) => [key, 'primary.light'],
);

const SHIPPING_STATUS_VALUES = [
	EEuDdpOrderShippingStatusName.UNFULFILLED,
	EEuDdpOrderShippingStatusName.S10_EXPECTED,
	EEuDdpOrderShippingStatusName.PROFORMA_SIGNED,
	EEuDdpOrderShippingStatusName.L_GENERATED,
	EEuDdpOrderShippingStatusName.WAITING_PROFORMA,
	EEuDdpOrderShippingStatusName.CUSTOMS_START,
	EEuDdpOrderShippingStatusName.CLEARED,
	EEuDdpOrderShippingStatusName.OMNIVA_PRINTED,
	EEuDdpOrderShippingStatusName.OMNIVA_DELIVERED,
	EEuDdpOrderShippingStatusName.S_REQUESTED,
	EEuDdpOrderShippingStatusName.PAYMENT_EXPECTED,
] as const;

export const SHIPPING_STATUS_FILTER: [string, string][] = SHIPPING_STATUS_VALUES.map((status) => [
	status,
	status,
]);

export const SHIPPING_STATUS_COLORS: [string, string][] = [
	[EEuDdpOrderShippingStatusName.UNFULFILLED, 'warning.light'],
	[EEuDdpOrderShippingStatusName.S10_EXPECTED, 'warning.light'],
	[EEuDdpOrderShippingStatusName.PROFORMA_SIGNED, 'primary.light'],
	[EEuDdpOrderShippingStatusName.CLEARED, 'primary.light'],
	[EEuDdpOrderShippingStatusName.L_GENERATED, 'primary.light'],
	[EEuDdpOrderShippingStatusName.WAITING_PROFORMA, 'warning.light'],
	[EEuDdpOrderShippingStatusName.CUSTOMS_START, 'primary.light'],
	[EEuDdpOrderShippingStatusName.CLEARED, 'primary.light'],
	[EEuDdpOrderShippingStatusName.OMNIVA_PRINTED, 'primary.light'],
	[EEuDdpOrderShippingStatusName.OMNIVA_DELIVERED, 'success.light'],
	[EEuDdpOrderShippingStatusName.S_REQUESTED, 'warning.light'],
	[EEuDdpOrderShippingStatusName.PAYMENT_EXPECTED, 'warning.light'],
];

export const EU_DDP_ACTIONS = {
	OPEN: 'Open Order',
	TRACKING: 'Tracking',
	GET_LAST_MILE_TRACKING: 'Get last mile tracking',
} as const;

export const LAST_MILE_TRACKING_STATUS_CODES: EEuDdpOrderShippingStatusCode[] = [
	EEuDdpOrderShippingStatusCode.CLEARED,
	EEuDdpOrderShippingStatusCode.OMNIVA_PRINTED,
];

export const hasLastMileTracking = (
	shippingStatusCode?: EEuDdpOrderShippingStatusCode | null,
): boolean => !!shippingStatusCode && LAST_MILE_TRACKING_STATUS_CODES.includes(shippingStatusCode);

export const getLastMileTrackingLink = (lastMileTrackingNumber?: string | null): string =>
	`https://minu.omniva.ee/track/${lastMileTrackingNumber ?? ''}?language=en`;

export const euDdpStructure: TTableStructure<
	keyof TEuDdpOrderResponseData,
	TEuDdpOrderResponseData
>[] = [
	{
		name: 'EAS Order Id',
		selector: 'id',
		show: true,
		sortable: true,
		filterable: false,
		colSpan: 1,
		type: ETableStructureType.LINK,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'EM ID',
		selector: 'em_id',
		show: false,
		sortable: true,
		filterable: false,
		colSpan: 1,
		type: ETableStructureType.STRING,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Date',
		selector: 'timestamp',
		show: true,
		sortable: true,
		filterable: true,
		filter: DATE_FILTER,
		colSpan: 1,
		type: ETableStructureType.DATE,
		align: ETableStructureAlign.CENTER,
	},
	{
		name: 'Store Order #',
		selector: 'external_order_id',
		show: true,
		sortable: true,
		filterable: false,
		searchable: true,
		colSpan: 1,
		type: ETableStructureType.STRING,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Delivery Country',
		selector: 'delivery_country',
		show: true,
		sortable: true,
		filterable: true,
		colSpan: 1,
		type: ETableStructureType.COUNTRY_CODE_SUPPORTED,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'VAT Schemes',
		selector: 'schemes',
		show: true,
		sortable: true,
		filterable: true,
		filter: SCHEMES_FILTER,
		colors: SCHEMES_COLORS,
		colSpan: 1,
		type: ETableStructureType.STATUS_ARRAY,
		align: ETableStructureAlign.LEFT,
		value: (item: TEuDdpOrderResponseData) =>
			(item?.schemes ? item.schemes.split(',') : []) as unknown as string,
	},
	{
		name: 'Total',
		selector: 'total_order_value_in_eur',
		show: true,
		sortable: true,
		filterable: false,
		colSpan: 1,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		breakdown: (item: TEuDdpOrderResponseData) =>
			createElement(AmountSpeedDialMemo, {
				merchandiseCost: item?.total_merch_cost ?? 0,
				deliveryCost: item?.delivery_cost ?? 0,
				taxes: item?.total_vat ?? 0,
				customDuties: item?.total_custom_duties ?? 0,
				easFee: item?.eas_fees_due ?? 0,
				customsProceduresFee: item?.total_dpo_fee ?? 0,
				orderCurrency: item?.payment_currency ?? '',
				thresholdCurrency: item?.threshold_currency ?? '',
				intrinsicThresholdExchangeRate: item?.intrinsic_threshold_exchange_rate ?? 0,
				importThreshold: `${item?.import_threshold ?? ''}`,
			}),
	},
	{
		name: 'Duties',
		selector: 'actual_customs_duties',
		show: true,
		sortable: true,
		filterable: false,
		colSpan: 1,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		value: (item: TEuDdpOrderResponseData) => {
			if (item?.values_status === 'Unprocessed') return '—';
			const amount =
				item?.values_status === 'Preliminary'
					? item?.preliminary_customs_duties
					: item?.actual_customs_duties;
			return String(amount ?? 0);
		},
		breakdown: (item: TEuDdpOrderResponseData) => {
			if (item?.values_status === 'Unprocessed') return null;
			return createElement(TableCellSpeedDial, {
				data: [
					{
						name: 'Preliminary customs duties',
						value: item?.preliminary_customs_duties ?? null,
					},
					{ name: 'Actual customs duties', value: item?.actual_customs_duties ?? null },
				],
				selectedRow: true,
			});
		},
	},
	{
		name: 'Import VAT',
		selector: 'actual_vat_amount',
		show: true,
		sortable: true,
		filterable: false,
		colSpan: 1,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		value: (item: TEuDdpOrderResponseData) => {
			if (item?.values_status === 'Unprocessed') return '—';
			const amount =
				item?.values_status === 'Preliminary'
					? item?.preliminary_vat_amount
					: item?.actual_vat_amount;
			return String(amount ?? 0);
		},
		breakdown: (item: TEuDdpOrderResponseData) => {
			if (item?.values_status === 'Unprocessed') return null;
			return createElement(TableCellSpeedDial, {
				data: [
					{ name: 'Preliminary vat amount', value: item?.preliminary_vat_amount ?? null },
					{ name: 'Actual vat amount', value: item?.actual_vat_amount ?? null },
				],
				selectedRow: true,
			});
		},
	},
	{
		name: 'Shipping Status',
		selector: 'shipping_status',
		show: true,
		sortable: true,
		filterable: true,
		filter: SHIPPING_STATUS_FILTER,
		colors: SHIPPING_STATUS_COLORS,
		colSpan: 1,
		type: ETableStructureType.STATUS,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Shipping Status',
		selector: 'shipping_status_id',
		show: false,
		sortable: false,
		filterable: false,
		colSpan: 1,
		type: ETableStructureType.STATUS,
		align: ETableStructureAlign.LEFT,
	},

	{
		name: 'Customs Processing Fee',
		selector: 'customs_fee',
		show: true,
		sortable: true,
		filterable: false,
		colSpan: 1,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
	},
	{
		name: 'Label Request Date',
		selector: 'label_requested_at',
		show: true,
		sortable: true,
		filterable: true,
		filter: DATE_FILTER,
		colSpan: 1,
		type: ETableStructureType.DATE,
		align: ETableStructureAlign.CENTER,
	},
	{
		name: 'Actions',
		selector: 'em_id',
		show: true,
		sortable: false,
		filterable: false,
		noName: true,
		colSpan: 1,
		type: ETableStructureType.BUTTONS,
		align: ETableStructureAlign.LEFT,
		buttonFilter: (item: TEuDdpOrderResponseData) => {
			if (item?.values_status === 'Unprocessed') {
				if (!item?.validate_hs_code && !item?.order_details_check) {
					return [{ name: SHIPMENTS_ACTIONS.REQUEST_DELIVERY, color: 'warning.main' }];
				}
				if (item?.order_details_check) {
					return [{ name: SHIPMENTS_ACTIONS.CHECK_ORDER, color: 'warning.main' }];
				}
				if (item?.validate_hs_code) {
					return [{ name: SHIPMENTS_ACTIONS.VALIDATE_HS, color: 'warning.main' }];
				}
				return [];
			}
			if (item?.shipping_status === 'Awaiting Signing') {
				return [
					{
						name:
							item?.schemes === 'IOSS-IMPORT'
								? SHIPMENTS_ACTIONS.UPLOAD_IOSS_INVOICE
								: SHIPMENTS_ACTIONS.SIGN_PROFORMA,
						color: 'warning.main',
					},
				];
			}
			if (item?.shipping_status_code === EEuDdpOrderShippingStatusCode.S_REQUESTED) {
				return [
					{
						name: SHIPMENTS_ACTIONS.CONFIRM_PAYMENT,
						color: 'warning.main',
					},
				];
			}
			if (item?.shipping_status_code === EEuDdpOrderShippingStatusCode.S10_EXPECTED) {
				return [
					{
						name: SHIPMENTS_ACTIONS.PROVIDE_S10_NUMBER,
						color: 'warning.main',
					},
				];
			}
			if (item?.shipping_status_code === EEuDdpOrderShippingStatusCode.PAYMENT_EXPECTED) {
				return [
					{
						name: SHIPMENTS_ACTIONS.CONFIRM_PAYMENT,
						color: 'warning.main',
					},
				];
			}
			return [
				{
					name: SHIPMENTS_ACTIONS.TRACKING,
					color: 'primary.main',
					icon: EIconButtons.LOCAL_SHIPPING,
					iconWithName: true,
				},
				...(hasLastMileTracking(item?.shipping_status_code) && item?.last_mile_tracking_number
					? [
							{
								name: SHIPMENTS_ACTIONS.GET_LAST_MILE_TRACKING,
								color: 'primary.main',
								icon: EIconButtons.PLACE,
								iconWithName: true,
							},
						]
					: []),
			];
		},
	},
];
